/**
 * placeholderEngine.js - Motor de Placeholders para Documentos Medicos
 * Usa sintaxe @PLACEHOLDER para substituicao dinamica
 */

// Categorias de placeholders
export const PLACEHOLDER_CATEGORIES = [
  {
    label: 'Dados do Paciente',
    items: [
      { key: 'PACIENTE', label: 'Nome do Paciente', example: 'JOAO DA SILVA' },
      { key: 'CPF_PACIENTE', label: 'CPF', example: '123.456.789-00' },
      { key: 'DATA_NASCIMENTO', label: 'Data de Nascimento', example: '01/01/1980' },
      { key: 'IDADE', label: 'Idade', example: '45 anos' },
      { key: 'SEXO', label: 'Sexo', example: 'Masculino' },
      { key: 'TELEFONE', label: 'Telefone', example: '(31) 99999-9999' },
      { key: 'ENDERECO', label: 'Endereco', example: 'Rua Principal, 100' },
      { key: 'CNS', label: 'Cartao SUS (CNS)', example: '700.0000.0000.0000' },
    ]
  },
  {
    label: 'Dados Clinicos',
    items: [
      { key: 'CID', label: 'CID-10', example: 'J06.9' },
      { key: 'DIAGNOSTICO', label: 'Diagnostico', example: 'Infeccao das vias aereas superiores' },
      { key: 'CONDUTA', label: 'Conduta', example: 'Repouso e medicacao sintomatica' },
      { key: 'QUEIXA_PRINCIPAL', label: 'Queixa Principal', example: 'Dor de cabeca ha 3 dias' },
      { key: 'HDA', label: 'Historia da Doenca Atual', example: 'Paciente refere...' },
      { key: 'EXAME_FISICO', label: 'Exame Fisico', example: 'BEG, corado, hidratado...' },
      { key: 'MEDICAMENTOS', label: 'Medicamentos em Uso', example: 'Losartana 50mg, Metformina 850mg' },
      { key: 'ALERGIAS', label: 'Alergias', example: 'Dipirona' },
      { key: 'OBSERVACOES', label: 'Observacoes', example: '' },
    ]
  },
  {
    label: 'Consulta',
    items: [
      { key: 'DATA', label: 'Data Atual', example: '06/03/2026' },
      { key: 'DATA_EXTENSO', label: 'Data por Extenso', example: '06 de marco de 2026' },
      { key: 'HORA', label: 'Hora Atual', example: '14:30' },
      { key: 'DIAS', label: 'Dias de Afastamento', example: '3' },
      { key: 'DIAS_EXTENSO', label: 'Dias por Extenso', example: 'tres' },
      { key: 'ESPECIALIDADE', label: 'Especialidade Encaminhada', example: 'Cardiologia' },
    ]
  },
  {
    label: 'Profissional',
    items: [
      { key: 'MEDICO', label: 'Nome do Medico', example: 'Dr. Carlos Santos' },
      { key: 'CRM', label: 'CRM/CRO', example: 'CRM/MG 12345' },
      { key: 'ESPECIALIDADE_MEDICO', label: 'Especialidade do Medico', example: 'Clinica Geral' },
    ]
  },
  {
    label: 'Unidade de Saude',
    items: [
      { key: 'CLINICA', label: 'Nome da Clinica/Unidade', example: 'UBS Centro' },
      { key: 'CNPJ_CLINICA', label: 'CNPJ', example: '11.466.385/0001-68' },
      { key: 'ENDERECO_CLINICA', label: 'Endereco da Clinica', example: 'Rua Prof. Cristiano Morais, 38' },
      { key: 'CIDADE_CLINICA', label: 'Cidade', example: 'Sao Domingos do Prata - MG' },
      { key: 'TELEFONE_CLINICA', label: 'Telefone da Clinica', example: '(31) 3868-1310' },
    ]
  }
];

// Flat list of all placeholder keys
export const ALL_PLACEHOLDERS = PLACEHOLDER_CATEGORIES.flatMap(cat => cat.items);

// Numbers in full text (Portuguese)
const NUMBERS_TEXT = {
  0: 'zero', 1: 'um', 2: 'dois', 3: 'tres', 4: 'quatro', 5: 'cinco',
  6: 'seis', 7: 'sete', 8: 'oito', 9: 'nove', 10: 'dez',
  11: 'onze', 12: 'doze', 13: 'treze', 14: 'quatorze', 15: 'quinze',
  16: 'dezesseis', 17: 'dezessete', 18: 'dezoito', 19: 'dezenove',
  20: 'vinte', 30: 'trinta', 45: 'quarenta e cinco', 60: 'sessenta', 90: 'noventa'
};

const MONTHS_PT = [
  'janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
];

function formatDateExtended(date) {
  const d = date || new Date();
  return `${d.getDate()} de ${MONTHS_PT[d.getMonth()]} de ${d.getFullYear()}`;
}

function formatDate(date) {
  const d = date || new Date();
  return d.toLocaleDateString('pt-BR');
}

function formatTime() {
  return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function calculateAge(birthDateStr) {
  if (!birthDateStr) return '';
  try {
    const parts = birthDateStr.split('/');
    let birth;
    if (parts.length === 3) {
      birth = new Date(parts[2], parts[1] - 1, parts[0]);
    } else {
      birth = new Date(birthDateStr);
    }
    if (isNaN(birth.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return `${age} anos`;
  } catch {
    return '';
  }
}

/**
 * Build context data from patient, doctor, and extra fields
 */
export function buildContextData({ patient, doctor, healthUnit, extraFields = {} }) {
  const now = new Date();
  const ctx = {};

  // Patient data
  if (patient) {
    ctx.PACIENTE = patient.nome || '';
    ctx.CPF_PACIENTE = patient.cpf || '';
    ctx.DATA_NASCIMENTO = patient.data_nascimento || '';
    ctx.IDADE = patient.data_nascimento ? calculateAge(patient.data_nascimento) : '';
    ctx.SEXO = patient.sexo || '';
    ctx.TELEFONE = patient.telefone || '';
    ctx.ENDERECO = patient.endereco || '';
    ctx.CNS = patient.cns || '';
  }

  // Doctor data
  if (doctor) {
    ctx.MEDICO = doctor.name || doctor.nome || '';
    ctx.CRM = doctor.crm || '';
    ctx.ESPECIALIDADE_MEDICO = doctor.especialidade || '';
  }

  // Health unit
  if (healthUnit) {
    ctx.CLINICA = healthUnit.nome || 'Fundo Municipal de Saude de Sao Domingos do Prata';
    ctx.CNPJ_CLINICA = healthUnit.cnpj || '11.466.385/0001-68';
    ctx.ENDERECO_CLINICA = healthUnit.endereco || 'Rua Prof. Cristiano Morais, 38 - Centro';
    ctx.CIDADE_CLINICA = healthUnit.cidade || 'Sao Domingos do Prata - MG';
    ctx.TELEFONE_CLINICA = healthUnit.telefone || '(31) 3868-1310';
  } else {
    ctx.CLINICA = 'Fundo Municipal de Saude de Sao Domingos do Prata';
    ctx.CNPJ_CLINICA = '11.466.385/0001-68';
    ctx.ENDERECO_CLINICA = 'Rua Prof. Cristiano Morais, 38 - Centro';
    ctx.CIDADE_CLINICA = 'Sao Domingos do Prata - MG';
    ctx.TELEFONE_CLINICA = '(31) 3868-1310';
  }

  // Date/time
  ctx.DATA = formatDate(now);
  ctx.DATA_EXTENSO = formatDateExtended(now);
  ctx.HORA = formatTime();

  // Extra fields override
  const days = extraFields.DIAS || extraFields.dias_afastamento;
  if (days) {
    ctx.DIAS = String(days);
    ctx.DIAS_EXTENSO = NUMBERS_TEXT[parseInt(days)] || String(days);
  }

  // Merge any extra fields (uppercase keys)
  for (const [key, value] of Object.entries(extraFields)) {
    if (value !== undefined && value !== null && value !== '') {
      ctx[key.toUpperCase()] = String(value);
    }
  }

  return ctx;
}

/**
 * Replace @PLACEHOLDER tokens in text with context data
 */
export function replacePlaceholders(template, contextData) {
  if (!template) return '';
  return template.replace(/@([A-Z_]+)/g, (match, key) => {
    return contextData[key] !== undefined && contextData[key] !== '' ? contextData[key] : match;
  });
}

/**
 * Check if text has any unresolved placeholders
 */
export function hasUnresolvedPlaceholders(text) {
  if (!text) return false;
  return /@[A-Z_]+/.test(text);
}

/**
 * Get list of unresolved placeholder keys from text
 */
export function getUnresolvedKeys(text, contextData) {
  if (!text) return [];
  const matches = text.match(/@([A-Z_]+)/g) || [];
  return [...new Set(matches.map(m => m.slice(1)))].filter(key => !contextData[key]);
}

/**
 * Default document templates with @ placeholders
 */
export const DEFAULT_DOCUMENT_TEMPLATES = [
  {
    tipo: 'atestado',
    titulo: 'Atestado Medico - Afastamento',
    descricao: 'Atestado para afastamento de atividades',
    corpo_template: `ATESTADO MEDICO

Atesto para os devidos fins que o(a) paciente @PACIENTE, portador(a) do CPF n. @CPF_PACIENTE, compareceu a este consultorio na data de hoje, @DATA, apresentando quadro clinico compativel com CID @CID, necessitando afastar-se de suas atividades por @DIAS (@DIAS_EXTENSO) dia(s), a partir desta data.

@CIDADE_CLINICA, @DATA_EXTENSO.


_____________________________________
@MEDICO
@CRM`
  },
  {
    tipo: 'declaracao',
    titulo: 'Declaracao de Comparecimento',
    descricao: 'Declaracao de comparecimento a consulta',
    corpo_template: `DECLARACAO DE COMPARECIMENTO

Declaro para os devidos fins que o(a) paciente @PACIENTE, portador(a) do CPF n. @CPF_PACIENTE, esteve presente neste estabelecimento de saude no dia @DATA, no horario das @HORA, para realizacao de consulta medica.

@CIDADE_CLINICA, @DATA_EXTENSO.


_____________________________________
@MEDICO
@CRM`
  },
  {
    tipo: 'encaminhamento',
    titulo: 'Encaminhamento Medico',
    descricao: 'Encaminhamento para especialista',
    corpo_template: `ENCAMINHAMENTO MEDICO

Encaminho o(a) paciente @PACIENTE, @IDADE, portador(a) do CPF n. @CPF_PACIENTE, para avaliacao e acompanhamento com @ESPECIALIDADE.

HIPOTESE DIAGNOSTICA:
@DIAGNOSTICO

HISTORIA CLINICA RESUMIDA:
@HDA

MEDICAMENTOS EM USO:
@MEDICAMENTOS

@CIDADE_CLINICA, @DATA_EXTENSO.


_____________________________________
@MEDICO
@CRM`
  },
  {
    tipo: 'laudo',
    titulo: 'Laudo Medico',
    descricao: 'Laudo medico detalhado',
    corpo_template: `LAUDO MEDICO

IDENTIFICACAO DO PACIENTE:
Nome: @PACIENTE
CPF: @CPF_PACIENTE
Data de Nascimento: @DATA_NASCIMENTO
Idade: @IDADE

DIAGNOSTICO:
@DIAGNOSTICO

CID-10: @CID

QUADRO CLINICO:
@EXAME_FISICO

CONDUTA:
@CONDUTA

@CIDADE_CLINICA, @DATA_EXTENSO.


_____________________________________
@MEDICO
@CRM`
  },
  {
    tipo: 'relatorio',
    titulo: 'Relatorio Medico',
    descricao: 'Relatorio medico completo',
    corpo_template: `RELATORIO MEDICO

Paciente: @PACIENTE
Idade: @IDADE
CPF: @CPF_PACIENTE

Queixa principal:
@QUEIXA_PRINCIPAL

Historia da doenca atual:
@HDA

Exame fisico:
@EXAME_FISICO

Diagnostico:
@DIAGNOSTICO

Conduta:
@CONDUTA

@CIDADE_CLINICA, @DATA_EXTENSO.


_____________________________________
@MEDICO
@CRM`
  }
];
